import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DEFAULT_THEME_ID, isThemeId, type ThemeId } from "@/theme";

interface ThemePrefState {
  /**
   * The user's own choice. `null` means they have never made one — distinct from having chosen
   * the same palette the tenant defaults to, because only the first should follow the house style
   * when an admin changes it.
   */
  themeId: ThemeId | null;
  /** Tenant default, cached from the API so a reload does not flash the wrong palette. */
  tenantDefaultThemeId: ThemeId;
  /** When false the tenant default is the only palette and the picker renders read-only. */
  allowUserThemeOverride: boolean;

  setThemeId: (id: ThemeId) => void;
  /** Called once the tenant settings land. Accepts raw API strings and validates them. */
  setTenantPolicy: (defaultThemeId: string, allowUserThemeOverride: boolean) => void;
}

/**
 * Appearance preference, levels 2 and 3 of the precedence chain:
 *
 *   1. `?theme=` query param — QA only, never persisted (see main.tsx)
 *   2. this user's choice
 *   3. the tenant default, which also *overrides* level 2 when the admin disallows overrides
 *   4. `DEFAULT_THEME_ID` — slate, so nothing shifts under existing users
 *
 * Persisted alongside `invmgmt-color-mode` and `invmgmt-density`, which it deliberately mirrors.
 * Note this store governs the theme only: light/dark and density stay purely per-user, because
 * they are legibility settings and a night-shift terminal has a different need from a daylit desk.
 */
export const useThemePref = create<ThemePrefState>()(
  persist(
    (set) => ({
      themeId: null,
      tenantDefaultThemeId: DEFAULT_THEME_ID,
      allowUserThemeOverride: true,
      setThemeId: (themeId) => set({ themeId }),
      setTenantPolicy: (defaultThemeId, allowUserThemeOverride) =>
        set({
          // An id the client does not ship (a newer build's theme, or a typo in the admin page)
          // degrades to slate rather than leaving buildTheme failing over on every render.
          tenantDefaultThemeId: isThemeId(defaultThemeId) ? defaultThemeId : DEFAULT_THEME_ID,
          allowUserThemeOverride,
        }),
    }),
    {
      name: "invmgmt-theme",
      version: 1,
      /**
       * v0 stored `themeId` as a plain ThemeId defaulting to "slate", with no way to tell a
       * deliberate choice from an untouched default. Carrying that forward verbatim would mean
       * every existing user counted as having chosen, and the tenant default would never reach
       * anyone. So a v0 "slate" is read as "never chose"; anything else was unambiguously picked.
       */
      migrate: (persisted, version) => {
        const prior = persisted as Partial<ThemePrefState> | undefined;
        if (version === 0) {
          const chosen = prior?.themeId;
          return {
            themeId: isThemeId(chosen) && chosen !== DEFAULT_THEME_ID ? chosen : null,
            tenantDefaultThemeId: DEFAULT_THEME_ID,
            allowUserThemeOverride: true,
          } as ThemePrefState;
        }
        return prior as ThemePrefState;
      },
      merge: (persisted, current) => {
        const p = persisted as Partial<ThemePrefState> | undefined;
        return {
          ...current,
          themeId: isThemeId(p?.themeId) ? p.themeId : null,
          tenantDefaultThemeId: isThemeId(p?.tenantDefaultThemeId)
            ? p.tenantDefaultThemeId
            : DEFAULT_THEME_ID,
          allowUserThemeOverride: p?.allowUserThemeOverride ?? true,
        };
      },
    }
  )
);

/**
 * The palette that should actually render, after precedence.
 *
 * When the tenant disallows overrides the default wins outright — but the user's own choice is
 * kept in storage rather than erased, so it returns if the admin re-enables the picker.
 */
export function useEffectiveThemeId(): ThemeId {
  const chosen = useThemePref((s) => s.themeId);
  const tenantDefault = useThemePref((s) => s.tenantDefaultThemeId);
  const mayOverride = useThemePref((s) => s.allowUserThemeOverride);
  if (!mayOverride) return tenantDefault;
  return chosen ?? tenantDefault;
}
